import { z } from "zod";
import { PrivateLessonInitialScheduleOnSellSchema } from "./bulk-schedule";

export const PrivateSharedHourSplitSchema = z
  .object({
    studioHours: z.number().int().min(0),
    nearHours: z.number().int().min(0),
    farHours: z.number().int().min(0),
  })
  .refine(
    (split) => split.studioHours + split.nearHours + split.farHours > 0,
    { message: "ຕ້ອງມີຊົ່ວໂມງຢ່າງໜ້ອຍ 1 ຊົ່ວໂມງ" },
  );

export type PrivateSharedHourSplitDTO = z.infer<
  typeof PrivateSharedHourSplitSchema
>;

/**
 * Schema สำหรับ Sell Private Lesson Package
 */
export const SellPrivateLessonPackageSchema = z
  .object({
    studentId: z.string().min(1, "ຕ້ອງເລືອກນັກຮຽນ"),
    templateId: z.string().min(1, "ຕ້ອງເລືອກແພັກເກດ"),
    /** ຈຳນວນຊົ່ວໂມງທີ່ຊື້ (ແມ່ແບບ = 1 ຊົ່ວໂມງຕໍ່ຫນ່ວຍລາຄາ) */
    purchasedHours: z
      .number()
      .int("ຈຳນວນຊົ່ວໂມງຕ້ອງເປັນຈຳນວນເຕັມ")
      .positive("ຕ້ອງຊື້ຢ່າງໜ້ອຍ 1 ຊົ່ວໂມງ"),
    /** ຈຳນວນວັນທີ່ໃຊ້ແພັກເກດໄດ້ນັບຈາກວັນເລີ່ມ (ກຳນົດຕອນຂາຍ) */
    validDays: z
      .number()
      .int("ຈຳນວນວັນຕ້ອງເປັນຈຳນວນເຕັມ")
      .min(1, "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ວັນ")
      .max(3650, "ຈຳນວນວັນຫຼາຍເກີນໄປ"),
    startDate: z.string().min(1, "ຕ້ອງເລືອກວັນທີເລີ່ມຕົ້ນ"),
    deactivateExisting: z.boolean().optional().default(false),
    discountType: z.enum(["percentage", "fixed"]).optional(),
    discountValue: z.number().optional(),
    settlementMode: z.enum(["receipt", "credit"]).optional().default("receipt"),
    amountPaidNow: z.coerce.number().int().min(0).optional().default(0),
    /** ຈອງຕາຕະລາງທັນທີຫຼັງສ້າງແພັກເກດ (ກວດຄວາມພ້ອມຝັ່ງ UI ກ່ອນສົ່ງ) */
    initialSchedule: PrivateLessonInitialScheduleOnSellSchema.optional(),
  })
  .refine(
    (data) => {
      // ถ้ามี discountType ต้องมี discountValue
      if (data.discountType && !data.discountValue) return false;
      // ถ้ามี discountValue ต้องมี discountType
      if (data.discountValue && !data.discountType) return false;
      // ถ้ามี discountType และ discountValue
      if (data.discountType && data.discountValue) {
        // percentage: ต้องอยู่ระหว่าง 0-100
        if (data.discountType === "percentage") {
          return data.discountValue >= 0 && data.discountValue <= 100;
        }
        // fixed: ต้อง > 0
        if (data.discountType === "fixed") {
          return data.discountValue > 0;
        }
      }
      return true;
    },
    {
      message: "ສ່ວນຫຼຸດບໍ່ຖືກຕ້ອງ",
    },
  );

export type SellPrivateLessonPackageDTO = z.infer<
  typeof SellPrivateLessonPackageSchema
>;

/**
 * Response สำหรับ Sell Private Lesson Package
 */
export const SellPrivateLessonPackageResponseSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  templateId: z.string(),
  totalHours: z.number(),
  remainingHours: z.number(),
  startDate: z.string(),
  expireDate: z.string(),
  status: z.string(),
  purchasedAt: z.string(),
  discountType: z.enum(["percentage", "fixed"]).nullable().optional(),
  discountValue: z.number().nullable().optional(),
  discountAmount: z.number().nullable().optional(),
  originalPrice: z.number().nullable().optional(),
  finalPrice: z.number().nullable().optional(),
  receiptId: z.string().optional(),
  credit: z
    .object({
      invoiceId: z.string(),
      totalAmount: z.number(),
      paidAmount: z.number(),
      remainingAmount: z.number(),
      status: z.string(),
    })
    .optional(),
});

export type SellPrivateLessonPackageResponseDTO = z.infer<
  typeof SellPrivateLessonPackageResponseSchema
>;

const sellDiscountRefine = (
  data: {
    discountType?: "percentage" | "fixed";
    discountValue?: number;
  },
  ctx: z.RefinementCtx,
) => {
  if (data.discountType && data.discountValue === undefined) {
    ctx.addIssue({
      code: "custom",
      message: "ຕ້ອງກຳນົດຄ່າສ່ວນຫຼຸດ",
      path: ["discountValue"],
    });
    return;
  }
  if (data.discountValue !== undefined && !data.discountType) {
    ctx.addIssue({
      code: "custom",
      message: "ຕ້ອງເລືອກປະເພດສ່ວນຫຼຸດ",
      path: ["discountType"],
    });
    return;
  }
  if (data.discountType && data.discountValue !== undefined) {
    if (data.discountType === "percentage") {
      if (data.discountValue < 0 || data.discountValue > 100) {
        ctx.addIssue({
          code: "custom",
          message: "ສ່ວນຫຼຸດເປີເຊັນຕ້ອງຢູ່ 0–100",
          path: ["discountValue"],
        });
      }
    } else if (data.discountValue <= 0) {
      ctx.addIssue({
        code: "custom",
        message: "ສ່ວນຫຼຸດຄົງທີ່ຕ້ອງເປັນຈຳນວນບວກ",
        path: ["discountValue"],
      });
    }
  }
};

/**
 * Schema สำหรับ Sell Private Lesson Group Package
 */
export const SellPrivateLessonGroupPackageSchema = z
  .object({
    templateId: z.string().min(1, "ຕ້ອງເລືອກແມ່ແບບກຸ່ມ"),
    ownerStudentId: z.string().min(1, "ຕ້ອງເລືອກຜູ້ຈ່າຍ/ເຈົ້າຂອງ"),
    memberStudentIds: z
      .array(z.string().min(1))
      .min(1, "ຕ້ອງເລືອກສະມາຊິກຢ່າງໜ້ອຍ 1 ຄົນ"),
    validDays: z
      .number()
      .int("ຈຳນວນວັນຕ້ອງເປັນຈຳນວນເຕັມ")
      .min(1, "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ວັນ")
      .max(3650, "ຈຳນວນວັນຫຼາຍເກີນໄປ"),
    startDate: z.string().min(1, "ຕ້ອງເລືອກວັນທີເລີ່ມຕົ້ນ"),
    deactivateExisting: z.boolean().optional().default(false),
    discountType: z.enum(["percentage", "fixed"]).optional(),
    discountValue: z.number().optional(),
    settlementMode: z.enum(["receipt", "credit"]).optional().default("receipt"),
    amountPaidNow: z.coerce.number().int().min(0).optional().default(0),
    initialSchedule: PrivateLessonInitialScheduleOnSellSchema.optional(),
  })
  .superRefine(sellDiscountRefine)
  .superRefine((data, ctx) => {
    const unique = new Set(data.memberStudentIds);
    if (unique.size !== data.memberStudentIds.length) {
      ctx.addIssue({
        code: "custom",
        message: "ສະມາຊິກຊ້ຳກັນໃນລາຍການ",
        path: ["memberStudentIds"],
      });
    }
    if (!data.memberStudentIds.includes(data.ownerStudentId)) {
      ctx.addIssue({
        code: "custom",
        message: "ຜູ້ຈ່າຍຕ້ອງເປັນຫນຶ່ງໃນສະມາຊິກກຸ່ມ",
        path: ["ownerStudentId"],
      });
    }
  });

export type SellPrivateLessonGroupPackageDTO = z.infer<
  typeof SellPrivateLessonGroupPackageSchema
>;

/**
 * Schema สำหรับ Sell Private Lesson Private Shared Package
 */
export const SellPrivateLessonPrivateSharedPackageSchema = z
  .object({
    templateId: z.string().min(1, "ຕ້ອງເລືອກແມ່ແບບ Private Package"),
    ownerStudentId: z.string().min(1, "ຕ້ອງເລືອກຜູ້ຈ່າຍ/ເຈົ້າຂອງ"),
    memberStudentIds: z
      .array(z.string().min(1))
      .min(1, "ຕ້ອງເລືອກສະມາຊິກຢ່າງໜ້ອຍ 1 ຄົນ"),
    purchasedHours: z
      .number()
      .int("ຈຳນວນຊົ່ວໂມງຕ້ອງເປັນຈຳນວນເຕັມ")
      .positive("ຕ້ອງຊື້ຢ່າງໜ້ອຍ 1 ຊົ່ວໂມງ"),
    validDays: z
      .number()
      .int("ຈຳນວນວັນຕ້ອງເປັນຈຳນວນເຕັມ")
      .min(1, "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ວັນ")
      .max(3650, "ຈຳນວນວັນຫຼາຍເກີນໄປ"),
    startDate: z.string().min(1, "ຕ້ອງເລືອກວັນທີເລີ່ມຕົ້ນ"),
    deactivateExisting: z.boolean().optional().default(false),
    discountType: z.enum(["percentage", "fixed"]).optional(),
    discountValue: z.number().optional(),
    settlementMode: z.enum(["receipt", "credit"]).optional().default("receipt"),
    amountPaidNow: z.coerce.number().int().min(0).optional().default(0),
    initialSchedule: PrivateLessonInitialScheduleOnSellSchema.optional(),
    /** แบ่งชม. studio / near / far เมื่อขายโดยไม่จอง schedule */
    hourSplit: PrivateSharedHourSplitSchema.optional(),
  })
  .superRefine(sellDiscountRefine)
  .superRefine((data, ctx) => {
    const unique = new Set(data.memberStudentIds);
    if (unique.size !== data.memberStudentIds.length) {
      ctx.addIssue({
        code: "custom",
        message: "ສະມາຊິກຊ້ຳກັນໃນລາຍການ",
        path: ["memberStudentIds"],
      });
    }
    if (!data.memberStudentIds.includes(data.ownerStudentId)) {
      ctx.addIssue({
        code: "custom",
        message: "ຜູ້ຈ່າຍຕ້ອງເປັນຫນຶ່ງໃນສະມາຊິກ",
        path: ["ownerStudentId"],
      });
    }
    if (data.initialSchedule && data.hourSplit) {
      ctx.addIssue({
        code: "custom",
        message: "ໃຊ້ schedule ຫຼື ແບ່ງຊົ່ວໂມງ — ບໍ່ໃຊ້ພ້ອມກັນ",
        path: ["hourSplit"],
      });
    }
    if (
      !data.initialSchedule &&
      data.hourSplit &&
      data.hourSplit.studioHours +
        data.hourSplit.nearHours +
        data.hourSplit.farHours !==
        data.purchasedHours
    ) {
      ctx.addIssue({
        code: "custom",
        message: "ລວມຊົ່ວໂມງ studio/ໃກ້/ໄກ ຕ້ອງເທົ່າຈຳນວນຊື້",
        path: ["hourSplit"],
      });
    }
  });

export type SellPrivateLessonPrivateSharedPackageDTO = z.infer<
  typeof SellPrivateLessonPrivateSharedPackageSchema
>;

/**
 * Schema สำหรับ Check Active Private Lesson Package
 */
export const CheckActivePrivateLessonPackageResponseSchema = z.object({
  hasActivePackage: z.boolean(),
  activePackage: z
    .object({
      id: z.string(),
      templateName: z.string(),
      remainingHours: z.number(),
      expireDate: z.string(),
      status: z.string(),
    })
    .nullable(),
});

export type CheckActivePrivateLessonPackageResponseDTO = z.infer<
  typeof CheckActivePrivateLessonPackageResponseSchema
>;
